import { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import {
  DollarSign, TrendingUp, TrendingDown, Clock, User, Search,
  Filter, RefreshCw, ChevronDown, ChevronUp, Plus, Edit,
  FileText, Receipt, AlertTriangle, CheckCircle, Minus, ExternalLink
} from 'lucide-react';
import { formatDate, formatDateTime, formatTime } from '../../utils/dateUtils';

export default function JobsPnL() {
  const [pnlData, setPnlData] = useState([]);
  const [handymen, setHandymen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterHandyman, setFilterHandyman] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedJob, setExpandedJob] = useState(null);
  
  // Dialog states
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [showTimesheetDialog, setShowTimesheetDialog] = useState(false);
  const [showRateDialog, setShowRateDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  
  // Forms
  const [expenseForm, setExpenseForm] = useState({
    expense_details: '',
    amount: '',
    bill_url: ''
  });
  const [timesheetForm, setTimesheetForm] = useState({
    punch_in_time: '',
    punch_out_time: '',
    notes: ''
  });
  const [rateForm, setRateForm] = useState({
    hourly_rate: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pnlRes, handymenRes] = await Promise.all([
        axiosInstance.get('/jobs/pnl'),
        axiosInstance.get('/handymen')
      ]);
      setPnlData(pnlRes.data);
      setHandymen(handymenRes.data);
    } catch (error) {
      toast.error('Failed to load P&L data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddExpense = async () => {
    if (!selectedJob || !expenseForm.expense_details || !expenseForm.amount) {
      toast.error('Please fill required fields');
      return;
    }
    
    try {
      await axiosInstance.post('/job-expenses', {
        job_id: selectedJob.job_id,
        expense_details: expenseForm.expense_details,
        amount: parseFloat(expenseForm.amount),
        bill_url: expenseForm.bill_url,
        is_manual: true,
        created_by: 'Admin'
      });
      
      toast.success('Expense added');
      setShowExpenseDialog(false);
      setExpenseForm({ expense_details: '', amount: '', bill_url: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add expense');
    }
  };

  const handleAddTimesheet = async () => {
    if (!selectedJob || !timesheetForm.punch_in_time || !timesheetForm.punch_out_time) {
      toast.error('Please fill required fields');
      return;
    }
    
    try {
      await axiosInstance.post('/timesheets/manual', {
        handyman_id: selectedJob.handyman_id,
        job_id: selectedJob.job_id,
        punch_in_time: new Date(timesheetForm.punch_in_time).toISOString(),
        punch_out_time: new Date(timesheetForm.punch_out_time).toISOString(),
        notes: timesheetForm.notes,
        created_by: 'Admin'
      });
      
      toast.success('Timesheet added');
      setShowTimesheetDialog(false);
      setTimesheetForm({ punch_in_time: '', punch_out_time: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add timesheet');
    }
  };

  const handleUpdateRate = async () => {
    if (!selectedJob || !rateForm.hourly_rate) {
      toast.error('Please enter hourly rate');
      return;
    }
    
    try {
      await axiosInstance.post(`/jobs/${selectedJob.job_id}/pnl-adjustment`, null, {
        params: {
          hourly_rate: parseFloat(rateForm.hourly_rate),
          notes: rateForm.notes
        }
      });
      
      toast.success('Hourly rate updated');
      setShowRateDialog(false);
      setRateForm({ hourly_rate: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to update rate');
    }
  };

  const filteredData = useMemo(() => {
    return pnlData.filter(job => {
      // Search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        if (!job.job_name?.toLowerCase().includes(term) &&
            !job.customer_name?.toLowerCase().includes(term) &&
            !job.handyman_name?.toLowerCase().includes(term)) {
          return false;
        }
      }
      
      // Handyman filter
      if (filterHandyman !== 'all' && job.handyman_id !== filterHandyman) {
        return false;
      }
      
      // Status filter
      if (filterStatus !== 'all' && job.status !== filterStatus) {
        return false;
      }
      
      return true;
    });
  }, [pnlData, searchTerm, filterHandyman, filterStatus]);

  const stats = useMemo(() => {
    const totalRevenue = filteredData.reduce((sum, j) => sum + (j.quote_amount || 0), 0);
    const totalCost = filteredData.reduce((sum, j) => sum + (j.total_cost || 0), 0);
    const totalProfit = filteredData.reduce((sum, j) => sum + (j.profit || 0), 0);
    const totalHours = filteredData.reduce((sum, j) => sum + (j.total_hours || 0), 0);
    const avgMargin = filteredData.length > 0 
      ? filteredData.reduce((sum, j) => sum + (j.margin_percent || 0), 0) / filteredData.length 
      : 0;
    
    const profitable = filteredData.filter(j => j.profit > 0).length;
    const unprofitable = filteredData.filter(j => j.profit < 0).length;
    
    return {
      totalRevenue,
      totalCost,
      totalProfit,
      totalHours,
      avgMargin: avgMargin.toFixed(1),
      profitable,
      unprofitable,
      total: filteredData.length
    };
  }, [filteredData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  // Using imported formatDate, formatDateTime, formatTime from dateUtils

  const formatTimeLocal = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleTimeString('en-US', { timeZone: 'America/Phoenix', hour: '2-digit', minute: '2-digit' });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500',
      assigned: 'bg-blue-500',
      in_progress: 'bg-orange-500',
      completed: 'bg-green-500',
      cancelled: 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getProfitColor = (profit) => {
    if (profit > 0) return 'text-green-600';
    if (profit < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  return (
    <div data-testid="jobs-pnl">
      <div className="mb-8">
        <h2 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Jobs P&L
        </h2>
        <p className="text-gray-600">Job-wise Profit & Loss Analysis</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
          <CardContent className="p-4">
            <div className="text-xs opacity-80">Total Jobs</div>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white border-0">
          <CardContent className="p-4">
            <div className="text-xs opacity-80">Total Revenue</div>
            <div className="text-xl font-bold">{formatCurrency(stats.totalRevenue)}</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0">
          <CardContent className="p-4">
            <div className="text-xs opacity-80">Total Cost</div>
            <div className="text-xl font-bold">{formatCurrency(stats.totalCost)}</div>
          </CardContent>
        </Card>
        <Card className={`bg-gradient-to-br ${stats.totalProfit >= 0 ? 'from-green-500 to-green-600' : 'from-red-500 to-red-600'} text-white border-0`}>
          <CardContent className="p-4">
            <div className="text-xs opacity-80">Total Profit</div>
            <div className="text-xl font-bold">{formatCurrency(stats.totalProfit)}</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
          <CardContent className="p-4">
            <div className="text-xs opacity-80">Total Hours</div>
            <div className="text-2xl font-bold">{stats.totalHours.toFixed(1)}h</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-teal-500 to-teal-600 text-white border-0">
          <CardContent className="p-4">
            <div className="text-xs opacity-80">Avg Margin</div>
            <div className="text-2xl font-bold">{stats.avgMargin}%</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
          <CardContent className="p-4">
            <div className="text-xs opacity-80">Profitable</div>
            <div className="text-2xl font-bold">{stats.profitable}</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white border-0">
          <CardContent className="p-4">
            <div className="text-xs opacity-80">Unprofitable</div>
            <div className="text-2xl font-bold">{stats.unprofitable}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
            <div className="space-y-2">
              <Label>Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Job, customer, handyman..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Handyman</Label>
              <Select value={filterHandyman} onValueChange={setFilterHandyman}>
                <SelectTrigger>
                  <SelectValue placeholder="All Handymen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Handymen</SelectItem>
                  {handymen.map(h => (
                    <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="assigned">Assigned</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 flex gap-2">
              <Button variant="outline" onClick={fetchData} className="flex-1">
                <RefreshCw size={16} className="mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* P&L Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#595959]"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredData.map((job) => (
            <Card key={job.job_id} className="backdrop-blur-sm bg-white/80 shadow-lg border-0">
              <Collapsible open={expandedJob === job.job_id}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <CardTitle className="text-xl">{job.job_name}</CardTitle>
                        <Badge className={`${getStatusColor(job.status)} text-white`}>
                          {job.status?.replace(/_/g, ' ').toUpperCase()}
                        </Badge>
                        <CollapsibleTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpandedJob(expandedJob === job.job_id ? null : job.job_id)}
                          >
                            {expandedJob === job.job_id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {job.customer_name} | {job.handyman_name || 'Unassigned'} | {formatDate(job.scheduled_date)}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className={`text-2xl font-bold ${getProfitColor(job.profit)}`}>
                        {job.profit >= 0 ? '+' : ''}{formatCurrency(job.profit)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {job.margin_percent}% margin
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardContent>
                  {/* Summary Row */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 py-3 px-4 bg-gray-50 rounded-lg mb-4">
                    <div>
                      <div className="text-xs text-gray-500">Quote Amount</div>
                      <div className="text-lg font-bold text-blue-600">{formatCurrency(job.quote_amount)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Labor ({job.total_hours}h × ${job.hourly_rate}/h)</div>
                      <div className="text-lg font-bold text-orange-600">{formatCurrency(job.labor_cost)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Expenses ({job.expenses?.length || 0})</div>
                      <div className="text-lg font-bold text-red-600">{formatCurrency(job.total_expenses)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Total Cost</div>
                      <div className="text-lg font-bold text-gray-700">{formatCurrency(job.total_cost)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Profit/Loss</div>
                      <div className={`text-lg font-bold ${getProfitColor(job.profit)}`}>
                        {formatCurrency(job.profit)}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 mb-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedJob(job);
                        setTimesheetForm({ punch_in_time: '', punch_out_time: '', notes: '' });
                        setShowTimesheetDialog(true);
                      }}
                    >
                      <Clock size={14} className="mr-1" />
                      Add Time Entry
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedJob(job);
                        setExpenseForm({ expense_details: '', amount: '', bill_url: '' });
                        setShowExpenseDialog(true);
                      }}
                    >
                      <Receipt size={14} className="mr-1" />
                      Add Expense
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedJob(job);
                        setRateForm({ hourly_rate: job.hourly_rate, notes: '' });
                        setShowRateDialog(true);
                      }}
                    >
                      <Edit size={14} className="mr-1" />
                      Edit Rate
                    </Button>
                  </div>

                  {/* Expanded Details */}
                  <CollapsibleContent>
                    <Tabs defaultValue="timesheets" className="mt-4">
                      <TabsList>
                        <TabsTrigger value="timesheets">Time Entries ({job.timesheets?.length || 0})</TabsTrigger>
                        <TabsTrigger value="expenses">Expenses ({job.expenses?.length || 0})</TabsTrigger>
                        <TabsTrigger value="timeline">Timeline ({job.timeline?.length || 0})</TabsTrigger>
                      </TabsList>

                      {/* Timesheets Tab */}
                      <TabsContent value="timesheets" className="mt-4">
                        <Card className="bg-gray-50">
                          <CardContent className="p-4">
                            {job.timesheets?.length > 0 ? (
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Start</TableHead>
                                    <TableHead>End</TableHead>
                                    <TableHead className="text-right">Hours</TableHead>
                                    <TableHead>Type</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {job.timesheets.map((ts, idx) => (
                                    <TableRow key={idx}>
                                      <TableCell>{formatDate(ts.punch_in_time)}</TableCell>
                                      <TableCell>{formatTime(ts.punch_in_time)}</TableCell>
                                      <TableCell>{ts.punch_out_time ? formatTime(ts.punch_out_time) : '-'}</TableCell>
                                      <TableCell className="text-right font-bold">{ts.total_hours || '-'}h</TableCell>
                                      <TableCell>
                                        {ts.is_manual ? (
                                          <Badge variant="outline">Manual</Badge>
                                        ) : (
                                          <Badge className="bg-blue-500 text-white">Auto</Badge>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            ) : (
                              <p className="text-gray-500 text-center py-4">No time entries recorded</p>
                            )}
                          </CardContent>
                        </Card>
                      </TabsContent>

                      {/* Expenses Tab */}
                      <TabsContent value="expenses" className="mt-4">
                        <Card className="bg-gray-50">
                          <CardContent className="p-4">
                            {job.expenses?.length > 0 ? (
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Description</TableHead>
                                    <TableHead>Added By</TableHead>
                                    <TableHead className="text-right">Amount</TableHead>
                                    <TableHead>Receipt</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {job.expenses.map((exp, idx) => (
                                    <TableRow key={idx}>
                                      <TableCell className="font-medium">{exp.expense_details}</TableCell>
                                      <TableCell>
                                        {exp.is_manual ? 'Admin (Manual)' : (exp.handyman_name || 'Unknown')}
                                      </TableCell>
                                      <TableCell className="text-right font-bold text-red-600">
                                        {formatCurrency(exp.amount)}
                                      </TableCell>
                                      <TableCell>
                                        {exp.bill_url ? (
                                          <a 
                                            href={exp.bill_url} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:underline flex items-center gap-1"
                                          >
                                            <ExternalLink size={12} /> View
                                          </a>
                                        ) : (
                                          <span className="text-gray-400">No receipt</span>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            ) : (
                              <p className="text-gray-500 text-center py-4">No expenses recorded</p>
                            )}
                          </CardContent>
                        </Card>
                      </TabsContent>

                      {/* Timeline Tab */}
                      <TabsContent value="timeline" className="mt-4">
                        <Card className="bg-gray-50">
                          <CardContent className="p-4">
                            {job.timeline?.length > 0 ? (
                              <div className="space-y-3">
                                {job.timeline.map((event, idx) => (
                                  <div key={idx} className="flex items-start gap-3">
                                    <div className={`w-3 h-3 rounded-full mt-1 ${
                                      event.event_type === 'completed_job' ? 'bg-green-500' :
                                      event.event_type === 'started_job' ? 'bg-orange-500' :
                                      event.event_type === 'started_travel' ? 'bg-blue-500' : 'bg-gray-500'
                                    }`} />
                                    <div>
                                      <p className="font-medium capitalize">
                                        {event.event_type.replace(/_/g, ' ')}
                                      </p>
                                      <p className="text-sm text-gray-500">
                                        {formatDate(event.timestamp)} at {formatTime(event.timestamp)}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-500 text-center py-4">No timeline events</p>
                            )}
                          </CardContent>
                        </Card>
                      </TabsContent>
                    </Tabs>
                  </CollapsibleContent>
                </CardContent>
              </Collapsible>
            </Card>
          ))}

          {filteredData.length === 0 && (
            <Card className="backdrop-blur-sm bg-white/80 shadow-lg border-0">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <DollarSign size={48} className="text-gray-300 mb-4" />
                <p className="text-gray-500 text-lg">No P&L data available</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Add Expense Dialog */}
      <Dialog open={showExpenseDialog} onOpenChange={setShowExpenseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Job Expense</DialogTitle>
            <DialogDescription>
              {selectedJob?.job_name} - {selectedJob?.customer_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Description *</Label>
              <Input
                placeholder="e.g., Materials, Tools, etc."
                value={expenseForm.expense_details}
                onChange={(e) => setExpenseForm({ ...expenseForm, expense_details: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Amount *</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={expenseForm.amount}
                onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Receipt/Bill URL</Label>
              <Input
                placeholder="https://... or paste URL"
                value={expenseForm.bill_url}
                onChange={(e) => setExpenseForm({ ...expenseForm, bill_url: e.target.value })}
              />
            </div>
            <Button className="w-full" onClick={handleAddExpense}>
              Add Expense
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Timesheet Dialog */}
      <Dialog open={showTimesheetDialog} onOpenChange={setShowTimesheetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Time Entry</DialogTitle>
            <DialogDescription>
              {selectedJob?.job_name} - {selectedJob?.handyman_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time *</Label>
                <Input
                  type="datetime-local"
                  value={timesheetForm.punch_in_time}
                  onChange={(e) => setTimesheetForm({ ...timesheetForm, punch_in_time: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>End Time *</Label>
                <Input
                  type="datetime-local"
                  value={timesheetForm.punch_out_time}
                  onChange={(e) => setTimesheetForm({ ...timesheetForm, punch_out_time: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Additional notes..."
                value={timesheetForm.notes}
                onChange={(e) => setTimesheetForm({ ...timesheetForm, notes: e.target.value })}
              />
            </div>
            <Button className="w-full" onClick={handleAddTimesheet}>
              Add Time Entry
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Hourly Rate Dialog */}
      <Dialog open={showRateDialog} onOpenChange={setShowRateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Hourly Rate</DialogTitle>
            <DialogDescription>
              Override hourly rate for {selectedJob?.job_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Hourly Rate ($/hour) *</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="25.00"
                value={rateForm.hourly_rate}
                onChange={(e) => setRateForm({ ...rateForm, hourly_rate: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Reason for rate change..."
                value={rateForm.notes}
                onChange={(e) => setRateForm({ ...rateForm, notes: e.target.value })}
              />
            </div>
            <Button className="w-full" onClick={handleUpdateRate}>
              Update Rate
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
